import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Wheat, Droplets, Sprout, Leaf, Beef, Milk } from 'lucide-react';
import { quotationsApi } from '../services/api';
import { Quotation } from '../types';
import '../styles/QuotationsSection.css';

const ICON_SIZE = 20;
const ICON_STROKE = 1.75;

const getCropIcon = (name: string): React.ReactNode => {
  const props = { size: ICON_SIZE, strokeWidth: ICON_STROKE };
  const map: { [key: string]: React.ReactNode } = {
    'soja':   <Sprout {...props} />,
    'arroz':  <Droplets {...props} />,
    'milho':  <Wheat {...props} />,
    'trigo':  <Wheat {...props} />,
    'feijão': <Leaf {...props} />,
    'boi':    <Beef {...props} />,
    'suíno':  <Milk {...props} />,
  };
  return map[name.toLowerCase()] ?? <Leaf {...props} />;
};

const QuotationsSection: React.FC = () => {
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        const data = await quotationsApi.getCurrentQuotations();
        setQuotations(data);
      } catch (error) {
        console.error('Error loading quotations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotations();
  }, []);

  const getChangeClass = (change: number | undefined) =>
    change === undefined || change === 0 ? 'stable' : change > 0 ? 'positive' : 'negative';

  const getChangeIcon = (change: number | undefined) =>
    change === undefined || change === 0 ? 'bi-arrow-right' : change > 0 ? 'bi-arrow-up' : 'bi-arrow-down';

  const getChangeLabel = (change: number | undefined) =>
    change === undefined || change === 0 ? 'Estável' : change > 0 ? 'Alta' : 'Baixa';

  return (
    <section className="quotations-section">
      <Container>
        <div className="section-header">
          <span className="section-badge">Mercado do Dia</span>
          <h2 className="section-title">Cotações Agricampanha</h2>
        </div>

        <Row className="g-5 justify-content-center mt-1">
          {loading ? (
            <>
              {[1, 2, 3].map((i) => (
                <Col key={i} md={6} lg={4}>
                  <div className="quotation-skeleton skeleton" />
                </Col>
              ))}
            </>
          ) : (
            quotations.map((item, index) => (
              <Col key={index} xs={10} sm={6} lg={4}>
                <div className="quotation-card">

                  {/* Imagem com overlay chapado e nome */}
                  <div className="quotation-img-wrapper">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="quotation-img" loading="lazy" />
                    ) : (
                      <div className="quotation-img-placeholder" />
                    )}
                    <div className="quotation-overlay" />
                    <h3 className="quotation-crop-name">{item.name}</h3>
                  </div>

                  {/* Ícone fora do wrapper para não ser cortado */}
                  <div className="quotation-icon-circle">
                    {getCropIcon(item.name)}
                  </div>

                  {/* Info: 2 colunas */}
                  <div className="quotation-info">
                    <div className="quotation-info-left">
                      <span className="quotation-unit-label">{item.unit}</span>
                      <span className="quotation-value">
                        R$ {item.value.toFixed(2).replace('.', ',')}
                      </span>
                    </div>
                    <div className="quotation-info-right">
                      <div className={`quotation-change ${getChangeClass(item.change)}`}>
                        <div className="quotation-change-arrow">
                          <i className={`bi ${getChangeIcon(item.change)}`}></i>
                        </div>
                        <div className="quotation-change-label">
                          {getChangeLabel(item.change)}
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </Col>
            ))
          )}
        </Row>

        <p className="text-center text-muted mt-3">
          <small>
            Cotações atualizadas com base no dia anterior antes do fechamento de mercado.
            Fonte: Cotações Agrícolas
          </small>
        </p>
      </Container>
    </section>
  );
};

export default QuotationsSection;
